import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WORKSPACE_STAGES,
  type WorkspaceFilters,
  type WorkspaceStage,
} from '@jobos/shared';

/**
 * Stage and every filter live in the URL, so `/invoices?stage=sent&q=acme`
 * is linkable and browser-back moves between stages (spec §6).
 *
 * Stage is one more filter dimension rather than four routes, which is exactly
 * why switching stage preserves the other filters — the whole reason the
 * workspace is one shell.
 */

/**
 * "Who owes us money" is the question the page exists to answer, so it opens on
 * Sent rather than To Bill.
 */
export const DEFAULT_STAGE: WorkspaceStage = 'sent';

export const SEARCH_DEBOUNCE_MS = 300;

/** URL param names. Shorter than the filter keys — these are user-visible. */
const PARAM = {
  stage: 'stage',
  jobId: 'job',
  search: 'q',
  dateFrom: 'from',
  dateTo: 'to',
  page: 'page',
} as const;

const isStage = (v: string | null): v is WorkspaceStage =>
  v !== null && (WORKSPACE_STAGES as readonly string[]).includes(v);

export interface UseInvoiceWorkspaceFilters {
  stage: WorkspaceStage;
  setStage: (stage: WorkspaceStage) => void;
  /** What the server is being asked for — search here is the DEBOUNCED value. */
  filters: WorkspaceFilters;
  /** What the search box shows — updates on every keystroke. */
  searchInput: string;
  setSearchInput: (value: string) => void;
  setJobId: (jobId: string | undefined) => void;
  setDateFrom: (date: string | undefined) => void;
  setDateTo: (date: string | undefined) => void;
  page: number;
  setPage: (page: number) => void;
  clear: () => void;
  activeFilterCount: number;
}

export function useInvoiceWorkspaceFilters(): UseInvoiceWorkspaceFilters {
  const [params, setParams] = useSearchParams();

  const stage = isStage(params.get(PARAM.stage)) ? (params.get(PARAM.stage) as WorkspaceStage) : DEFAULT_STAGE;
  const urlSearch = params.get(PARAM.search) ?? '';

  // The input is local so typing is immediate; the URL catches up on a debounce.
  const [searchInput, setSearchInput] = useState(urlSearch);

  // Keep the box in step when the URL moves underneath it — browser-back, or a
  // link pasted into the bar. Guarded on the URL value so this does not fight
  // the debounce while someone is typing.
  const lastUrlSearch = useRef(urlSearch);
  useEffect(() => {
    if (urlSearch !== lastUrlSearch.current) {
      lastUrlSearch.current = urlSearch;
      setSearchInput(urlSearch);
    }
  }, [urlSearch]);

  /**
   * `replace: true` throughout. Every filter change would otherwise push a
   * history entry, and typing six characters would put six of them between the
   * user and the page they arrived from. Stage changes push, because moving
   * between stages IS the navigation browser-back should undo.
   */
  const write = useCallback(
    (mutate: (next: URLSearchParams) => void, opts: { replace?: boolean } = { replace: true }) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        opts,
      );
    },
    [setParams],
  );

  const setParam = useCallback(
    (key: string, value: string | undefined) => {
      write((next) => {
        if (value) next.set(key, value);
        else next.delete(key);
        // Any filter change invalidates the current page offset — page 4 of a
        // narrower result set is usually empty, which reads as "no results".
        if (key !== PARAM.page) next.delete(PARAM.page);
      });
    },
    [write],
  );

  // Debounce search into the URL so typing neither spams history nor requests.
  useEffect(() => {
    if (searchInput === urlSearch) return;
    const t = setTimeout(() => {
      lastUrlSearch.current = searchInput;
      setParam(PARAM.search, searchInput || undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput, urlSearch, setParam]);

  const setStage = useCallback(
    (next: WorkspaceStage) => {
      write(
        (p) => {
          p.set(PARAM.stage, next);
          // Every other param survives — filters sit above stage (FR-2.3).
          p.delete(PARAM.page);
        },
        { replace: false },
      );
    },
    [write],
  );

  const filters = useMemo<WorkspaceFilters>(() => {
    const f: WorkspaceFilters = {};
    const jobId = params.get(PARAM.jobId);
    const dateFrom = params.get(PARAM.dateFrom);
    const dateTo = params.get(PARAM.dateTo);
    if (jobId) f.jobId = jobId;
    if (urlSearch) f.search = urlSearch;
    if (dateFrom) f.dateFrom = dateFrom;
    if (dateTo) f.dateTo = dateTo;
    return f;
  }, [params, urlSearch]);

  const pageParam = Number(params.get(PARAM.page));
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const clear = useCallback(() => {
    setSearchInput('');
    lastUrlSearch.current = '';
    // Stage survives a filter clear — clearing filters is not leaving the page.
    write((p) => {
      for (const key of [PARAM.jobId, PARAM.search, PARAM.dateFrom, PARAM.dateTo, PARAM.page]) {
        p.delete(key);
      }
    });
  }, [write]);

  return {
    stage,
    setStage,
    filters,
    searchInput,
    setSearchInput,
    setJobId: useCallback((v) => setParam(PARAM.jobId, v), [setParam]),
    setDateFrom: useCallback((v) => setParam(PARAM.dateFrom, v), [setParam]),
    setDateTo: useCallback((v) => setParam(PARAM.dateTo, v), [setParam]),
    page,
    setPage: useCallback((n: number) => setParam(PARAM.page, n > 1 ? String(n) : undefined), [setParam]),
    clear,
    activeFilterCount: Object.keys(filters).length,
  };
}
